import React from "react";
import { styled, Box } from "@mui/material";
import Typography, { TypographyProps } from "@common/components/Typography";

/**
 * @mui/styled implementation of https://codesandbox.io/s/zen-galileo-spy6yy?file=/src/App.js
 * One of the goal is to make it adapt to the size the container has available for it
 * instead of having fixed pixel sizes
 */

const TypoSpan = (props: TypographyProps) => (
  <Typography
    {...props}
    variant="caption"
    sx={{
      textAlign: "center",
    }}
    realTime
  />
);
const Gauge = styled(Box)(({ theme }) => ({
  maxHeight: "100%",
  flex: 1,
}));
const Title = styled(Typography)(({ theme }) => ({
  width: "100%",
  textAlign: "center",
}));
const Container = styled(Box)(({ theme }) => ({
  position: "relative",
  aspectRatio: "2/1",
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.highlighter,
  borderRadius: "100000px 100000px 0 0",
  overflow: "hidden",
  textAlign: "center",
}));
const Percentage = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: "-200%",
  width: "400%",
  height: "400%",
  marginLeft: "50%",
  backgroundColor: theme.palette.graph.mustard,
  transform: "rotate(0deg)",
  transition: "transform 1s",
  transformOrigin: "top center",
}));

const gaugeWidth = "10%";
const Mask = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: gaugeWidth,
  right: `calc(${gaugeWidth} / 2)`,
  left: `calc(${gaugeWidth} / 2)`,
  // Adding a hard coded 2px to handle visual glitches due to how the sizes are rounded once it needs to be displayed
  height: `calc(100% - ${gaugeWidth} + 2px)`,
  backgroundColor: theme.palette.background.default,
  borderRadius: "10000px 10000px 0 0",
}));

const Value = styled((props: TypographyProps) => (
  <TypoSpan {...props} size="big" fontWeight={700} />
))(({ theme }) => ({
  position: "absolute",
  bottom: "0",
  left: "0",
  width: "100%",
}));
const Min = styled(TypoSpan)(({ theme }) => ({
  float: "left",
  color: theme.palette.common.grey.g9B9B9B,
}));
const Max = styled(TypoSpan)(({ theme }) => ({
  float: "right",
  color: theme.palette.common.grey.g9B9B9B,
}));
const UnitContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  textAlign: "center",
}));
const Unit = styled(TypoSpan)(({ theme }) => ({
  color: theme.palette.common.grey.g9B9B9B,
  position: "absolute",
  right: 0,
  width: "100%",
}));

type ChartProps = {
  value: number;
  min: number;
  max: number;
  unit: string;
  title: string | string[];
};

const Chart = (props: ChartProps) => {
  const { min, max, value, unit, title } = props;
  const titles = Array.isArray(title) ? title : [title];
  const bounded = Math.max(min, Math.min(value, max));
  const percent = bounded / max;
  const col: "success" | "error" | "warning" =
    percent < 1 / 3 ? "error" : percent < 2 / 3 ? "warning" : "success";
  return (
    <Gauge>
      {titles.map((title, index) => (
        <Title
          key={index}
          reserveSpace
          sx={index === titles.length - 1 ? [{ marginBottom: 2 }] : []}
        >
          {title}
        </Title>
      ))}
      <Container>
        <Percentage
          sx={({ palette }) => ({ backgroundColor: palette[col].main })}
          style={{ transform: `rotate(${percent * 180}deg)` }}
        />
        <Mask />
        <Value>{value}</Value>
      </Container>
      <Min>{min}</Min>
      <Max>{max}</Max>
      <UnitContainer>
        <Unit>{unit}</Unit>
      </UnitContainer>
    </Gauge>
  );
};

export default Chart;
